import { environment } from "../../config/environment";
import React, { useState, useEffect } from "react";
import { customFetch } from "../../utils/Auth";
import { CLIENT_LOG_EVENTS, CLIENT_LOG_EVENTS_INFO, CLIENT_LOG_EVENTS_ERROR } from "../../config/client-logs-events";
import ClientEventsTables from "./ClientEventsTables";
import { Toaster } from "../ui/Toaster";

const ClientLogs = ({ allDoctors, setAllDoctors }) => {
  const [doctorSearch, setDoctorSearch] = useState("");
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [patientId, setPatientId] = useState("");
  const [eventSearch, setEventSearch] = useState("");
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTimeRange, setSelectedTimeRange] = useState("");
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [showSearchForm, setShowSearchForm] = useState(false);

  const TIME_RANGE_OPTIONS = [
    { value: "5m", label: "Last 5 minutes" },
    { value: "10m", label: "Last 10 minutes" },
    { value: "1h", label: "Last hour" },
    { value: "3h", label: "Last 3 hours" },
    { value: "6h", label: "Last 6 hours" },
    { value: "24h", label: "Last 24 hours" },
    { value: "7d", label: "Last 7 days" },
  ];

  // Calculate timestamps based on selected date or time range
  const getTimestamps = () => {
    let startTimestamp = null;
    let endTimestamp = null;

    if (selectedTimeRange) {
      // Calculate based on time range (relative to now)
      const now = Math.floor(Date.now() / 1000);
      const minutesMap = {
        "5m": 5,
        "10m": 10,
        "1h": 60,
        "3h": 180,
        "6h": 360,
        "24h": 1440,
        "7d": 10080, // 7 days * 24 hours * 60 minutes
      };
      const minutes = minutesMap[selectedTimeRange];
      if (minutes === undefined) {
        console.error('Unknown time range:', selectedTimeRange);
        return { startTimestamp: null, endTimestamp: null };
      }
      startTimestamp = now - (minutes * 60);
      endTimestamp = now;
    } else if (selectedDate) {
      // Calculate for the selected date (entire day in local timezone)
      // Parse the date string (YYYY-MM-DD) and create date in local timezone
      const [year, month, day] = selectedDate.split('-').map(Number);
      const startOfDay = new Date(year, month - 1, day, 0, 0, 0);
      const endOfDay = new Date(year, month - 1, day, 23, 59, 59);
      startTimestamp = Math.floor(startOfDay.getTime() / 1000);
      endTimestamp = Math.floor(endOfDay.getTime() / 1000);
    }

    return { startTimestamp, endTimestamp };
  };

  // Fetch all doctors when component mounts
  useEffect(() => {
    if (allDoctors.length === 0) {
      (async () => {
        try {
          const response = await customFetch(`${environment.apiUrl}/doctors/all-doctors`, { method: "GET" });
          const data = await response.json();
          setAllDoctors(Array.isArray(data) ? data : []);
        } catch (err) {
          setAllDoctors([]);
        }
      })();
    }
  }, [allDoctors.length, setAllDoctors]);

  // Filter doctors as user types
  useEffect(() => {
    if (doctorSearch.trim()) {
      const search = doctorSearch.trim().toLowerCase();
      setFilteredDoctors(
        allDoctors.filter(doc => {
          const name = doc.user_name?.toLowerCase() || "";
          const id = doc.user_id?.toLowerCase() || "";
          const email = doc.user_email?.toLowerCase() || "";
          return name.includes(search) || id.includes(search) || email.includes(search);
        })
      );
    } else {
      setFilteredDoctors([]);
    }
  }, [doctorSearch, allDoctors]);

  // Filter events as user types (exclude already selected events)
  // Search across both INFO and ERROR events
  useEffect(() => {
    if (eventSearch.trim()) {
      const search = eventSearch.trim().toLowerCase();
      const selectedEventSet = new Set(selectedEvents);
      const allEvents = [...CLIENT_LOG_EVENTS_INFO, ...CLIENT_LOG_EVENTS_ERROR];
      setFilteredEvents(
        allEvents.filter(event => {
          // Don't show already selected events
          if (selectedEventSet.has(event)) return false;
          return event.toLowerCase().includes(search);
        })
      );
    } else {
      setFilteredEvents([]);
    }
  }, [eventSearch, selectedEvents]);

  const handleEventSelect = (event) => {
    if (!selectedEvents.includes(event)) {
      setSelectedEvents(prev => [...prev, event]);
      setEventSearch("");
    }
  };

  const handleEventRemove = (eventToRemove) => {
    setSelectedEvents(prev => prev.filter(e => e !== eventToRemove));
  };

  // Extract search logic into a reusable function
  const performSearch = async (skipValidation = false) => {
    const { startTimestamp, endTimestamp } = getTimestamps();
    
    // Debug logging
    if (selectedTimeRange) {
      console.log('Time range selected:', selectedTimeRange);
      console.log('Start timestamp:', startTimestamp, new Date(startTimestamp * 1000).toISOString());
      console.log('End timestamp:', endTimestamp, new Date(endTimestamp * 1000).toISOString());
      console.log('Duration:', (endTimestamp - startTimestamp) / 60, 'minutes');
    }
    
    // Validate (skip for auto-search)
    if (!skipValidation) {
      // Ensure only one of doctor or patient ID is set (mutually exclusive)
      if (selectedDoctor && patientId.trim()) {
        alert("Please select either Doctor Name or Patient ID, not both");
        return;
      }
    }

    setIsLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (selectedDoctor?.user_name) {
        params.append('userName', selectedDoctor.user_name);
      }
      if (patientId.trim()) {
        params.append('patientId', patientId.trim());
      }
      if (selectedEvents.length > 0) {
        params.append('events', selectedEvents.join(','));
      }
      if (startTimestamp) {
        params.append('startTimestamp', startTimestamp.toString());
      }
      if (endTimestamp) {
        params.append('endTimestamp', endTimestamp.toString());
      }

      const response = await customFetch(
        `${environment.apiUrl}/doctors/client-logs/search?${params.toString()}`,
        { method: "GET" }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch client logs');
      }

      const data = await response.json();
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching client logs:', err);
      setError('Failed to fetch client logs: ' + err.message);
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    await performSearch(false);
  };

  // Auto-search on mount for last hour
  useEffect(() => {
    // Set time range to "last hour"
    setSelectedTimeRange("1h");
    
    // Set default events to filter by
    const defaultEvents = ['ERROR_LOGIN', 'ERROR_MICROPHONE_PERMISSION'];
    setSelectedEvents(defaultEvents);
    
    // Calculate timestamps for last hour directly
    const now = Math.floor(Date.now() / 1000);
    const oneHourAgo = now - (60 * 60); // 60 minutes * 60 seconds
    
    // Perform auto-search with last hour filter
    const performAutoSearch = async () => {
      setIsLoading(true);
      setError(null);
      setHasSearched(true);

      try {
        const params = new URLSearchParams();
        params.append('startTimestamp', oneHourAgo.toString());
        params.append('endTimestamp', now.toString());
        params.append('events', defaultEvents.join(','));

        const response = await customFetch(
          `${environment.apiUrl}/doctors/client-logs/search?${params.toString()}`,
          { method: "GET" }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch client logs');
        }

        const data = await response.json();
        setLogs(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching client logs:', err);
        setError('Failed to fetch client logs: ' + err.message);
        setLogs([]);
      } finally {
        setIsLoading(false);
      }
    };

    performAutoSearch();
  }, []); // Only run on mount


  // Get active filters for display
  const getActiveFilters = () => {
    const filters = [];
    
    if (selectedDoctor?.user_name) {
      filters.push({ label: 'Doctor', value: selectedDoctor.user_name });
    }
    
    if (patientId.trim()) {
      filters.push({ label: 'Patient ID', value: patientId.trim() });
    }
    
    if (selectedEvents.length > 0) {
      filters.push({ label: 'Events', value: selectedEvents.join(', ') });
    }
    
    if (selectedTimeRange) {
      const timeRangeOption = TIME_RANGE_OPTIONS.find(opt => opt.value === selectedTimeRange);
      if (timeRangeOption) {
        filters.push({ label: 'Time Range', value: timeRangeOption.label });
      }
    } else if (selectedDate) {
      filters.push({ label: 'Date', value: new Date(selectedDate).toLocaleDateString() });
    }
    
    return filters;
  };

  const activeFilters = getActiveFilters();

  // Check if search button should be disabled
  // Require at least one of: selectedDoctor, patientId, or selectedEvents
  const isSearchDisabled = !selectedDoctor && !patientId.trim() && selectedEvents.length === 0;

  return (
    <div className="w-full py-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Client Events</h2>
        <button
          type="button"
          onClick={() => setShowSearchForm(!showSearchForm)}
          className="px-4 py-2 bg-ktx-purple text-white rounded hover:bg-purple-700 transition-colors text-sm"
        >
          {showSearchForm ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>
      
      {/* Active Filters Display */}
      {activeFilters.length > 0 && (
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
          <div className="text-xs font-semibold text-gray-600 mb-2">Active Filters:</div>
          <div className="flex flex-wrap gap-2">
            {activeFilters.map((filter, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded text-xs bg-white border border-gray-300 text-gray-700"
              >
                <span className="font-medium">{filter.label}:</span>
                <span className="ml-1">{filter.value}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      
      {showSearchForm && (
        <form onSubmit={handleSearch} className="space-y-6 mb-6">
        {/* Doctor Name and Patient ID Search */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="clientLogsDoctorSearch" className="block text-sm font-medium text-gray-700 mb-2">
              Doctor Name
            </label>
            <input
              id="clientLogsDoctorSearch"
              type="text"
              autoComplete="off"
              value={doctorSearch}
              onChange={e => {
                setDoctorSearch(e.target.value);
                if (e.target.value.trim()) {
                  setPatientId(""); // Clear patient ID when doctor search starts
                }
              }}
              disabled={!!patientId}
              placeholder="Type to search for a doctor..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple mb-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            {doctorSearch && filteredDoctors.length > 0 && (
              <div className="w-full max-h-56 overflow-y-auto border border-gray-200 rounded-md bg-white shadow z-10">
                {filteredDoctors.map(doc => (
                  <div
                    key={doc.user_id}
                    className={`px-4 py-2 cursor-pointer hover:bg-purple-100 ${selectedDoctor && selectedDoctor.user_id === doc.user_id ? 'bg-purple-200' : ''}`}
                    onClick={() => {
                      setSelectedDoctor(doc);
                      setDoctorSearch("");
                      setPatientId(""); // Clear patient ID when doctor is selected
                    }}
                  >
                    <div className="font-semibold text-gray-900">
                      {doc.user_name} {doc.user_email ? <span className="text-xs text-gray-500">({doc.user_email})</span> : null}
                    </div>
                    <div className="text-xs text-gray-500">{doc.user_id}</div>
                  </div>
                ))}
              </div>
            )}
            {selectedDoctor && (
              <div className="mt-2 text-sm text-gray-600">
                Selected: <span className="font-semibold">{selectedDoctor.user_name}</span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDoctor(null);
                    setDoctorSearch("");
                    // Note: patientId can now be set since doctor is cleared
                  }}
                  className="ml-2 text-ktx-purple underline"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="patientIdSearch" className="block text-sm font-medium text-gray-700 mb-2">
              Patient ID
            </label>
            <input
              id="patientIdSearch"
              type="text"
              autoComplete="off"
              value={patientId}
              onChange={e => {
                setPatientId(e.target.value);
                if (e.target.value.trim()) {
                  setSelectedDoctor(null); // Clear selected doctor when patient ID is entered
                  setDoctorSearch(""); // Clear doctor search
                }
              }}
              disabled={!!selectedDoctor}
              placeholder="Enter patient ID..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            {patientId && (
              <button
                type="button"
                onClick={() => {
                  setPatientId("");
                  // Note: doctor can now be set since patient ID is cleared
                }}
                className="mt-2 text-sm text-ktx-purple underline"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Event Selection */}
        <div>
          <label htmlFor="eventSearch" className="block text-sm font-medium text-gray-700 mb-2">
            Events
          </label>
          <div className="relative">
            <input
              id="eventSearch"
              type="text"
              autoComplete="off"
              value={eventSearch}
              onChange={e => setEventSearch(e.target.value)}
              placeholder="Type to search for events..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple"
            />
            {eventSearch && filteredEvents.length > 0 && (
              <div className="absolute z-10 w-full mt-1 max-h-56 overflow-y-auto border border-gray-200 rounded-md bg-white shadow">
                {filteredEvents.map(event => {
                  const isError = CLIENT_LOG_EVENTS_ERROR.includes(event);
                  const isInfo = CLIENT_LOG_EVENTS_INFO.includes(event);
                  return (
                    <div
                      key={event}
                      className="px-4 py-2 cursor-pointer hover:bg-purple-100"
                      onClick={() => handleEventSelect(event)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-900">{event}</div>
                        {isError && (
                          <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-800 font-medium">
                            ERROR
                          </span>
                        )}
                        {isInfo && (
                          <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-medium">
                            INFO
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {eventSearch && filteredEvents.length === 0 && (
              <div className="absolute z-10 w-full mt-1 border border-gray-200 rounded-md bg-white shadow">
                <div className="px-4 py-2 text-sm text-gray-400">No matching events found.</div>
              </div>
            )}
          </div>
          {selectedEvents.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedEvents.map(event => (
                <span
                  key={event}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-ktx-purple text-white"
                >
                  {event}
                  <button
                    type="button"
                    onClick={() => handleEventRemove(event)}
                    className="ml-2 hover:text-gray-200 focus:outline-none"
                    aria-label={`Remove ${event}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              <button
                type="button"
                onClick={() => {
                  setSelectedEvents([]);
                  setEventSearch("");
                }}
                className="text-sm text-ktx-purple underline"
              >
                Clear All
              </button>
            </div>
          )}
        </div>

        {/* Date and Time Range Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="selectedDate" className="block text-sm font-medium text-gray-700 mb-2">
              Date
            </label>
            <input
              id="selectedDate"
              type="date"
              value={selectedDate}
              onChange={e => {
                setSelectedDate(e.target.value);
                setSelectedTimeRange(""); // Clear time range when date is selected
              }}
              disabled={!!selectedTimeRange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            {selectedDate && (
              <p className="mt-1 text-xs text-gray-500">
                Will search for events on this date
              </p>
            )}
          </div>
          <div>
            <label htmlFor="selectedTimeRange" className="block text-sm font-medium text-gray-700 mb-2">
              Time Range
            </label>
            <select
              id="selectedTimeRange"
              value={selectedTimeRange}
              onChange={e => {
                setSelectedTimeRange(e.target.value);
                setSelectedDate(""); // Clear date when time range is selected
              }}
              disabled={!!selectedDate}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Select time range...</option>
              {TIME_RANGE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {selectedTimeRange && (
              <p className="mt-1 text-xs text-gray-500">
                Will search for events in the {TIME_RANGE_OPTIONS.find(o => o.value === selectedTimeRange)?.label.toLowerCase()}
              </p>
            )}
          </div>
        </div>

        {/* Search Button */}
        <div>
          <button
            type="submit"
            disabled={isLoading || isSearchDisabled}
            className="px-6 py-2 bg-ktx-purple text-white rounded hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={isSearchDisabled ? "Please set at least one of: Doctor Name, Patient ID, or Events" : ""}
          >
            {isLoading ? "Searching..." : "Search"}
          </button>
        </div>
      </form>
      )}

      {/* Results */}
      {isLoading && (
        <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-gray-600">Loading logs...</p>
        </div>
      )}

      {error && (
        <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {!isLoading && !error && hasSearched && logs.length === 0 && (
        <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-gray-600">No logs found matching the search criteria.</p>
        </div>
      )}

      {!isLoading && !error && logs.length > 0 && (
        <ClientEventsTables logs={logs} isLoading={isLoading} error={error} />
      )}
      <Toaster />
    </div>
  );
};

export default ClientLogs;

